<template>
  <div class="fts-lite-users inistate-scroller">
    <div>
      <div class="users__title">User management</div>
      <div class="users__content">
        <div class="user-control">
          <div 
            class="user-control-action" 
            :class="{'user-control-action--active': tab == 'active'}" 
            @click="setTab('active')"
          >
            Users
          </div>
          <div 
            class="user-control-action" 
            :class="{'user-control-action--active': tab == 'lobby'}" 
            @click="setTab('lobby')"
          >
            Invites
          </div>
        </div>

        <template v-if="tab == 'lobby'">
          <div class="users__content-control" v-if="lobbyFormData.isEnabled && lobbyFormData.cloneInvitationCode">
            <fts-clipboard class="users__content-invitation-link" :copy="getInviteMessage">
              {{lobbyFormData.invitationLink}}
            </fts-clipboard>

            <fts-clipboard class="users__content-invitation-code" :copy="lobbyFormData.cloneInvitationCode">
              Code: {{lobbyFormData.cloneInvitationCode}}
            </fts-clipboard>
          </div>

          <table class="fts-data-table">
            <thead class="data-table__head">
              <tr class="data-table__row">
                <th class="data-table__cell data-table__cell--header">
                  Name
                </th>

                <th class="data-table__cell data-table__cell--header">
                  Email
                </th>

                <th class="data-table__cell data-table__cell--header">
                  Action
                </th>
              </tr>
            </thead>
            <tbody class="data-table__body">
              <tr 
                v-for="user in users" 
                :key="user.key" 
                class="data-table__row"
              >
                <td class="data-table__cell">
                  <fts-profile
                    :profile="{
                      displayName: user.displayName || user.userName,
                      profilePictureUrl: user.profilePictureUrl,
                    }" 
                    :profile-photo-size="24"
                  >
                  </fts-profile>
                </td>

                <td class="data-table__cell">
                  {{user.email}}
                </td>

                <td class="data-table__cell">
                  <div>
                    <fts-btn
                      color="primary" 
                      outline 
                      text-transform-init 
                      @click="acceptUser(user)"
                    >
                      Accept
                    </fts-btn>

                    <fts-btn
                      color="danger" 
                      outline 
                      text-transform-init 
                      @click="rejectUser(user)"
                    >
                      Reject
                    </fts-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </template>

        <template v-else>
          <div class="users__content-control">
            <input
              type="text"
              class="fts-form-control users__content-search"
              placeholder="Search by name"
              v-model.trim="searchDisplayName"
              @input="search"
            />
          </div>

          <table class="fts-data-table">
            <thead class="data-table__head">
              <tr class="data-table__row">
                <th class="data-table__cell data-table__cell--header">
                  Name
                </th>

                <th class="data-table__cell data-table__cell--header">
                  Email
                </th>

                <th class="data-table__cell data-table__cell--header">
                  Contact
                </th>

                <th class="data-table__cell data-table__cell--header">
                  Role
                </th>

                <th class="data-table__cell data-table__cell--header">
                  Status
                </th>
              </tr>
            </thead>
            <tbody class="data-table__body">
              <tr 
                v-for="user in users" 
                :key="user.key" 
                class="data-table__row"
              >
                <td class="data-table__cell">
                  <fts-profile
                    :profile="{
                      displayName: user.displayName || user.userName,
                      profilePictureUrl: user.profilePictureUrl,
                    }" 
                    :profile-photo-size="24"
                  >
                  </fts-profile>
                </td>

                <td class="data-table__cell">
                  {{user.email}}
                </td>

                <td class="data-table__cell">
                  {{user.phoneNumber}}
                </td>

                <td class="data-table__cell">
                  <div class="user-profiles">                    
                    <fts-label-group 
                      v-for="x in getUserProfiles(user.profiles)" 
                      :key="x.id" 
                      :item="x" 
                      class="user-profile"
                    ></fts-label-group>
                  </div>
                </td>

                <td class="data-table__cell">
                  <div class="user-action-wrapper">
                    <div 
                      class="user-status" 
                      :class="user.status === 'active' ? 'user-status--active' : 'user-status--inactive'"
                    >{{user.status}}</div>

                    <fts-dropdown-select
                      class="user-action-dropdown"
                      append-content-class="dropdown-menu dropdown-menu-w-sm user-action-content user-action-dropdown-menu"
                      :offset="[0, 6]"
                      placement="left-start"
                      @selected="executeAction($event, user)"
                    >
                      <fts-btn 
                        slot="toggle" 
                        sm 
                        icon 
                        text 
                        class="user-action-toggle"
                      >
                        <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
                      </fts-btn>
                      <template #menu="{ select, defaultOptionClass }">
                        <!-- reset password -->
                        <div
                          class="user-action-option"
                          :class="defaultOptionClass"
                          @click="select('resetPassword')"
                        >
                          <div class="user-action-option-icon">
                            <font-awesome-icon :icon="['fas', 'key']" fixed-wdith />
                          </div>
                          <div class="user-action-option-r">
                            <div class="user-action-option-label">Reset Password</div>
                            <div class="user-action-option-helper">
                              Request to reset a new password.
                            </div>
                          </div>
                        </div>
                        <!-- enable member -->
                        <div
                          class="user-action-option"
                          :class="defaultOptionClass"
                          @click="select('disable')"
                          v-if="user.disabled"
                        >
                          <div class="user-action-option-icon">
                            <font-awesome-icon :icon="['fas', 'user-check']" />
                          </div>
                          <div class="user-action-option-r">
                            <div class="user-action-option-label">Enable {{member.label}}</div>
                            <div class="user-action-option-helper">
                              {{member.description}}
                            </div>
                          </div>
                        </div>
                        <!-- disable member -->
                        <div
                          class="user-action-option"
                          :class="defaultOptionClass"
                          @click="select('disable')"
                          v-else
                        >
                          <div class="user-action-option-icon">
                            <font-awesome-icon :icon="['fas', 'user-times']" fixed-width />
                          </div>
                          <div class="user-action-option-r">
                            <div class="user-action-option-label">Disable {{member.label}}</div>
                            <div class="user-action-option-helper">
                              {{member.description}}
                            </div>
                          </div>
                        </div>
                        <!-- unlock user -->
                        <div
                          class="user-action-option"
                          :class="defaultOptionClass"
                          @click="select('lock')"
                          v-if="user.isLocked"
                        >
                          <div class="user-action-option-icon">
                            <font-awesome-icon :icon="['fas', 'unlock']" fixed-width />
                          </div>
                          <div class="user-action-option-r">
                            <div class="user-action-option-label">Unlock User</div>
                            <div class="user-action-option-helper">
                              Activate the user to access the workspace.
                            </div>
                          </div>
                        </div>
                        <!-- consultant -->
                        <template v-if="isAdministrator">
                          <div
                            class="user-action-option"
                            :class="defaultOptionClass"
                            @click="select('consultant')"
                            v-if="!user.consultant"
                          >
                            <div class="user-action-option-icon">
                              <font-awesome-icon :icon="['fas', 'user-check']" fixed-width />
                            </div>
                            <div class="user-action-option-r">
                              <div class="user-action-option-label">
                                Set as {{consultant.label}}
                              </div>
                              <div class="user-action-option-helper">
                                {{consultant.description}}
                              </div>
                            </div>
                          </div>
                          <!-- remove workspace admin -->
                          <div
                            class="user-action-option"
                            :class="defaultOptionClass"
                            @click="select('consultant')"
                            v-else
                          >
                            <div class="user-action-option-icon">
                              <font-awesome-icon :icon="['fas', 'user-times']" fixed-width />
                            </div>
                            <div class="user-action-option-r">
                              <div class="user-action-option-label">
                                Remove {{consultant.label}}
                              </div>
                              <div class="user-action-option-helper">
                                {{consultant.description}}
                              </div>
                            </div>
                          </div>
                        </template>
                        <!-- workspace admin -->
                        <template v-if="isAdminUser">             
                          <div
                            class="user-action-option"
                            :class="defaultOptionClass"
                            @click="select('workspaceAdmin')"
                            v-if="!user.workspaceAdmin"
                          >
                            <div class="user-action-option-icon">
                              <font-awesome-icon :icon="['fas', 'user-check']" fixed-width />
                            </div>
                            <div class="user-action-option-r">
                              <div class="user-action-option-label">
                                Set as {{workspaceAdmin.label}}
                              </div>
                              <div class="user-action-option-helper">
                                {{workspaceAdmin.description}}
                              </div>
                            </div>
                          </div>
                          <!-- remove workspace admin -->
                          <div
                            class="user-action-option"
                            :class="defaultOptionClass"
                            @click="select('workspaceAdmin')"
                            v-else
                          >
                            <div class="user-action-option-icon">
                              <font-awesome-icon :icon="['fas', 'user-times']" fixed-width />
                            </div>
                            <div class="user-action-option-r">
                              <div class="user-action-option-label">
                                Remove {{workspaceAdmin.label}}
                              </div>
                              <div class="user-action-option-helper">
                                {{workspaceAdmin.description}}
                              </div>
                            </div>
                          </div>
                        </template>
                        <!-- user admin -->
                        <template v-if="isAdminUser || isManagerUser">
                          <div
                            class="user-action-option"
                            :class="defaultOptionClass"
                            @click="select('userAdmin')"
                            v-if="!user.userAdmin"
                          >
                            <div class="user-action-option-icon">
                              <font-awesome-icon :icon="['fas', 'user-check']" fixed-width />
                            </div>
                            <div class="user-action-option-r">
                              <div class="user-action-option-label">
                                Set as {{userAdmin.label}}
                              </div>
                              <div class="user-action-option-helper">
                                {{userAdmin.description}}
                              </div>
                            </div>
                          </div>
                          <!-- remove user admin -->
                          <div
                            class="user-action-option"
                            :class="defaultOptionClass"
                            @click="select('userAdmin')"
                            v-else
                          >
                            <div class="user-action-option-icon">
                              <font-awesome-icon :icon="['fas', 'user-times']" fixed-width />
                            </div>
                            <div class="user-action-option-r">
                              <div class="user-action-option-label">
                                Remove {{userAdmin.label}}
                              </div>
                              <div class="user-action-option-helper">
                                {{userAdmin.description}}
                              </div>
                            </div>
                          </div>
                        </template>
                      </template>
                    </fts-dropdown-select>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </div>

    <!-- reset password modal -->
    <fts-client-modal
      title="Request Reset Password"
      @closed="resetModal"
      ref="resetPasswordModal"
    >
      <!-- header:icon -->
      <font-awesome-icon slot="icon" :icon="['fas', 'key']" fixed-width />
      <!-- body -->
      <template #body>
        <!-- temporary password -->
        <template v-if="modalFormData.temporaryPassword">
          <div class="confirmation-desc">
            Temporary password for {{ modalFormData.userName }} is:
          </div>
          <fts-clipboard :copy="modalFormData.temporaryPassword" class="confirmation-clipboard">
            {{modalFormData.temporaryPassword}}
          </fts-clipboard>
        </template>
        <!-- request -->
        <template v-else>
          <div class="confirmation-desc">
            This will replace {{ modalFormData.userName }}'s current password with
            a new password.
          </div>
          <fts-btn
            color="primary"
            outline
            text-transform-init
            :loading="modalLoading"
            class="confirmation-action"
            @click="resetPassword"
          >
            Request New Password
          </fts-btn>
        </template>
      </template>
      <!-- footerRight -->
      <template #footerRight>
        <fts-btn
          text
          :disabled="modalLoading"
          @click="closeResetPasswordModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>
      </template>
    </fts-client-modal>
    <!-- user unlock modal -->
    <fts-client-modal
      title="Unlock user account"
      @closed="resetModal"
      ref="userUnlockModal"
    >
      <!-- header:icon -->
      <font-awesome-icon slot="icon" :icon="['fas', 'unlock']" fixed-width />
      <!-- body -->
      <template #body>
        <div class="confirmation-desc">
          It will allow {{ modalFormData.userName }} access to this workspace.
        </div>
      </template>
      <!-- footerRight -->
      <template #footerRight>
        <fts-btn
          text
          :disabled="modalLoading"
          @click="closeUserUnlockModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>

        <fts-btn
          color="primary" 
          :loading="modalLoading" 
          @click="unlock"
        >
          <font-awesome-icon :icon="['fas', 'unlock']" /> Unlock
        </fts-btn>
      </template>
    </fts-client-modal>
    <!-- user access modal -->
    <fts-client-modal
      :title="modalFormData.disabled ? 'Enable user for access?' : 'Disable user for access?'"
      @closed="resetModal"
      ref="userAccessModal"
    >
      <!-- header:icon -->
      <font-awesome-icon slot="icon" :icon="['fas', 'user-check']" fixed-width v-if="modalFormData.disabled" />
      <font-awesome-icon slot="icon" :icon="['fas', 'user-times']" fixed-width v-else />
      <!-- body -->
      <template #body>
        <template v-if="modalFormData.disabled">
          <div class="confirmation-desc">
            It will allow {{ modalFormData.userName }} access to this workspace.
          </div>
          <div class="confirmation-subtext">
            You can block access by disable the member later.
          </div>
        </template>

        <template v-else>
          <div class="confirmation-desc">
            It will block {{ modalFormData.userName }}'s access to this
            workspace.
          </div>
          <div class="confirmation-subtext">
            You can restore access by enable the member later.
          </div>
        </template>
      </template>
      <!-- footerRight -->
      <template #footerRight>
        <fts-btn
          text
          :disabled="modalLoading"
          @click="closeUserAccessModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>

        <fts-btn
          color="primary"
          :loading="modalLoading"
          @click="setDisableEnable"
        >
          <template v-if="modalFormData.disabled">
            <font-awesome-icon :icon="['fas', 'user-check']" /> Enable
          </template>
          <template v-else>
            <font-awesome-icon :icon="['fas', 'user-times']" /> Disable
          </template>
        </fts-btn>
      </template>
    </fts-client-modal>
    <!-- spinner -->
    <fts-spinner full-screen boundary="parent" v-if="loading" />
  </div>
</template>

<script>
import Vue from 'vue';
import { clientStore } from '../store/clientStore';

import FTSBtn from '../elements/FTSBtn';
import FTSClientModal from '../elements/FTSClientModal';
import FTSClipboard from '../elements/FTSClipboard';
import FTSDropdownSelect from '../elements/FTSDropdownSelect';
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSProfile from '../elements/FTSProfile';
import FTSSpinner from '../elements/FTSSpinner';
import { authorizationStore } from '../store/authorizationStore';

export default {
  data: () => ({
    tab: "active",
    users: [],
    searchDisplayName: "",
    loading: false,
    error: false,
    modalFormData: {},
    modalLoading: false,
    lobbyFormData: {
      isEnabled: false,
      invitationCode: "",
      cloneInvitationCode: "",
      invitationLink: "",
    }
  }),
  components: {
    "fts-btn": FTSBtn,
    "fts-client-modal": FTSClientModal,
    "fts-clipboard": FTSClipboard,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-profile": FTSProfile,
    "fts-spinner": FTSSpinner
  },
  methods: {
    async loadLobby() {
      this.loading = true;
      this.error = false;

      try {
        const res = await clientStore.dispatch("loadLobby", {});
        this.users = res.data.users;
        this.lobbyFormData.isEnabled = res.data.enable;
        this.lobbyFormData.invitationCode = res.data.code;
        this.lobbyFormData.cloneInvitationCode = res.data.code;
        this.lobbyFormData.invitationLink = res.data.lobbyLink;
      } catch(err) {
        this.users = [];
        this.error = true;
        this.$toasted.error("Failed to retrieve users");
      } finally {
        this.loading = false;
      }
    },
    async loadMember() {
      this.loading = true;
      this.error = false;

      try {
        const res = await clientStore.dispatch("getMember", {
          searchDisplayName: this.searchDisplayName,
          type: this.tab // active; inactive
        });

        this.users = res.data.map(x => ({
          ...x,
          status: x.disabled ? "inactive" : "active"
        }));
      } catch(err) {
        this.users = [];
        this.error = true;
        this.$toasted.error("Failed to retrieve users");
      } finally {
        this.loading = false;
      }
    },
    async getMember() {
      if (this.tab == "lobby") {
        await this.loadLobby();
      } else {
        await this.loadMember();
      }
    },
    search: _.debounce(async function() {
      await this.getMember();
    }),
    getInviteMessage() {
      return "You're invited to join the Inistate workspace " + clientStore.getters.workspaceName + "!\n" +
        "\n" + 
        "Join now: " + this.lobbyFormData.invitationLink + "\n" +
        "\n" +
        "Get the app: https://inistate.com/download\n" +
        "Your code: " + this.lobbyFormData.cloneInvitationCode;
    },
    getUserProfiles(profiles) {
      return this.workspaceProfiles.reduce((list, x) => {
        const userProfile = profiles.find((y) => y.moduleId == x.id);
        if (userProfile) {
          list.push({
            id: x.id,
            name: x.name,
            emoji: x.emoji,
            entryId: userProfile.entryId,
            active: userProfile.active,
          });
        }
        return list;
      }, []);
    },
    setTab(value) {
      if (value == this.type) return;

      this.tab = value;
      this.getMember();
    },
    async executeAction(type, selectedItem) {
      if (
        type == "workspaceAdmin" ||
        type == "userAdmin" ||
        type == "consultant"
      ) {
        await clientStore.dispatch("setRole", {
          userProfileId: selectedItem.userProfileId,
          workspaceAdmin:
            type == "workspaceAdmin"
              ? !selectedItem.workspaceAdmin
              : selectedItem.workspaceAdmin,
          userAdmin:
            type == "userAdmin"
              ? !selectedItem.userAdmin
              : selectedItem.userAdmin,
          consultant:
            type == "consultant"
              ? !selectedItem.consultant
              : selectedItem.consultant,
        });
        await this.getMember();
        this.$toasted.success(`User role have been updated`);
      } else if (type == "resetPassword") {
        this.modalFormData = _.clone(selectedItem);
        this.$refs.resetPasswordModal.show();
      } else if (type == "lock") {
        this.modalFormData = _.clone(selectedItem);
        this.$refs.userUnlockModal.show();
      } else if (type == "disable") {
        this.modalFormData = _.clone(selectedItem);
        this.$refs.userAccessModal.show();
      }
    },
    async resetPassword() {
      this.modalLoading = true;
      try {
        let res = await clientStore.dispatch("resetUserPassword", {
          id: this.modalFormData.userProfileId,
        });
        this.modalFormData.temporaryPassword = res.data.password;
        this.$toasted.success("Password reset successfully");
      } catch (err) {
        this.$toasted.global.error({
          response: err.response,
          message: "Failed to reset password",
        });
        this.closeResetPasswordModal();
      }
      this.modalLoading = false;
    },
    async unlock() {
      this.modalLoading = true;
      try {
        await clientStore.dispatch("unlockUser", {
          id: this.modalFormData.userProfileId,
        });
        await this.getMember();
        this.$toasted.success(
          `User access has been ${
            this.modalFormData.isLocked ? "unlock" : "locked"
          }`
        );
      } catch (err) {
        this.$toasted.global.error({
          response: err.response,
          message: "Failed to update user access",
        });
      }
      this.closeUserUnlockModal();
      this.modalLoading = false;
    },
    async setDisableEnable() {
      this.modalLoading = true;
      try {
        await clientStore.dispatch("setDisableEnable", {
          id: this.modalFormData.userProfileId,
        });
        await this.getMember();
        this.$toasted.success(
          `User access has been ${
            this.modalFormData.disabled ? "granted" : "denied"
          }`
        );
      } catch (err) {
        this.$toasted.global.error({
          response: err.response,
          message: "Failed to update user access",
        });
      }
      this.closeUserAccessModal();
      this.modalLoading = false;
    },
    closeResetPasswordModal() {
      this.$refs.resetPasswordModal.close();
    },
    closeUserUnlockModal() {
      this.$refs.userUnlockModal.close();
    },
    closeUserAccessModal() {
      this.$refs.userAccessModal.close();
    },
    resetModal() {
      this.modalFormData = {};
    },
    async acceptUser(user) {
      try {
        await clientStore.dispatch("acceptRejectUser", {
          userId: user.userProfileId,
        });

        await this.loadLobby();
        this.$toasted.success(`User ${user.userName} has been successfully accepted into the workspace`);
      } catch(err) {
        this.$toasted.global.error(err);
      }
    },
    async rejectUser(user) {
      try {
        await clientStore.dispatch("acceptRejectUser", {
          userId: user.userProfileId,
          reject: true
        });

        await this.loadLobby();
        this.$toasted.success(`User ${user.userName} has been rejected into the workspace. To revert, please go to disabled members to enable`);
      } catch(err) {
        this.$toasted.global.error(err);
      }
    }
  },
  computed: {
    isAdministrator() {
      return authorizationStore.getters.isAdministrator;
    },
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    isManagerUser() {
      return authorizationStore.getters.isManagerUser;
    },
    workspaceProfiles() {
      return clientStore.getters.workspaceModules.filter(x => x.isProfile);
    }
  },
  beforeMount() {
    if (!(authorizationStore.getters.isAdminUser || authorizationStore.getters.isManagerUser)) {
      this.$toasted.error("Access Denied. Please refer to your system administrator.");
      this.$router.push({ name: "lite" });
    }
  },
  created() {
    this.roles = [
      { id: "consultant", name: "Consultant"},
      { id: "workspaceAdmin", name: "Workspace Admin"},
      { id: "userAdmin", name: "User Admin"}
    ];
    this.consultant = {
      label: "Consultant",
      description: "Consultant can access studio, change workspace settings and invite new members to the workspace."
    };
    this.workspaceAdmin = {
      label: "Workspace Admin",
      description: "Workspace Admin can access studio, change workspace settings and invite new members to the workspace."
    };
    this.userAdmin = {
      label: "User Admin",
      description: "User Admin can invite new members to the workspace."
    };
    this.member = {
      label: "Member",
      description: "Member cannot change workspce settings or invite new members to the workspace."
    };
  },
  async mounted() {
    await this.getMember();
    window.eventBus.$on("users:refresh", this.getMember);
  },
  beforeDestroy() {
    window.eventBus.$off("users:refresh", this.getMember);
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-lite-users {
  flex: 1;
  padding: 1rem;  
  .users__title {
    margin-bottom: 1rem;
    font-weight: var(--inistate-font-weight-bold);
    font-size: var(--inistate-heading-text-20);
    line-height: var(--inistate-heading-text-20--line-height);
  }
  .users__content {
    display: inline-flex;
    flex-direction: column;
    min-width: 100%;
    padding: 1rem;
    border-radius: var(--inistate-rounded-8);
    background-color: #fff;
  }

  .users__content-control {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    > * {
      &:not(:first-child) {
        margin-left: 1rem;
      }
    }
  }
  .users__content-search {
    width: 330px;
    border-radius: var(--inistate-rounded-4);
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    background-color: #fff;
  }
  .users__content-invitation-link {
    width: 380px;
    border-radius: var(--inistate-rounded-4);
    white-space: nowrap;
    overflow: hidden;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    background-color: #fff !important;
  }
  .users__content-invitation-code {
    width: 160px;
    border-radius: var(--inistate-rounded-4);
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    background-color: #fff !important;
  }

  .user-control {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    border-bottom: 1px solid var(--fts-border);
  }
  .user-control-action {
    @include user-select-none;
    position: relative;
    padding-bottom: 1rem;
    font-weight: var(--inistate-font-weight-bold);
    font-size: var(--inistate-heading-text-16);
    line-height: var(--inistate-heading-text-16--line-height);
    &::after {
      content: "";
      position: absolute;
      left: 0;
      bottom: -1px;
      width: 100%;
      border-bottom: 2px solid transparent;
    }
    &:not(:first-child) {
      margin-left: 1.25rem;;
    }
    &:hover {
      color: var(--fts-primary);
      cursor: pointer;
    }
    &.user-control-action--active {
      color: var(--fts-primary);
      &::after {
        border-color: var(--fts-primary);
      }
    }
  }
  .user-profiles {
    @include flex-gap($gap: 0.5rem);
  }
  .user-profile {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    border-radius: var(--inistate-rounded-8);
    background-color: var(--fts-gray-200);
  }
  .user-status {
    margin-right: 0.75rem;
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    border-radius: var(--inistate-rounded-8);
    background-color: var(--fts-gray-200);
    text-transform: capitalize;
    color: #fff;
    &.user-status--active {
      background-color: var(--inistate-color-green-700);
    }
    &.user-status--inactive {
      background-color: var(--inistate-color-red-700);
    }
  }
  .user-action-wrapper {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }
  .user-action-dropdown {
    margin-top: -0.25rem;
  }

  // data table
  .fts-data-table {
    width: 100%;
    border-collapse: collapse;
    .data-table__head {
      .data-table__row {
        background-color: var(--inistate-color-neutral-50);
      }

      .data-table__cell {
        color: var(--inistate-text-secondary-color);
        white-space: nowrap;
        vertical-align: middle;
      }

      .data-table__label-group {
        align-items: center;
      }
    }
    .data-table__body {
      .data-table__row {
        border-bottom: 1px solid var(--fts-border);
      }
    }
    .data-table__row {
      &.data-table__row--selected {
        background-color: rgba(var(--inistate-color-primary-500-rgb), 0.085);
      }

      &:hover {
        .data-table__cell-shortcut {
          display: block;
        }
      }
    }
    .data-table__cell {
      padding: 0.5rem 0.75rem;
      font-weight: normal;
      font-size: var(--inistate-label-text-14);
      line-height: var(--inistate-paragraph-text-14--line-height);
      text-align: left;
      vertical-align: top;
      color: var(--inistate-text-primary-color);
      // white-space: nowrap;
      position: relative;
      &.data-table__cell--primary-cell {
        position: relative;
        padding-right: 4.5rem;
      }
      &.data-table__cell--design {
        cursor: pointer;
        &:hover {
          .data-table__cell-design-delete {
            visibility: visible;
          }
        }
      }
      &.data-table__cell--design-control {
        min-width: 200px;
      }
      &.data-table__cell--header {
        &:not(.data-table__cell--design) {
          padding-right: 44px;
        }
      }
    }
  }
  // confirmation modal
  .confirmation-desc {
    font-size: 1rem;
    line-height: 1.5rem;
  }
  .confirmation-subtext {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-default);
  }
  .confirmation-action,
  .confirmation-clipboard {
    margin-top: 0.375rem;
  }
}

.user-action-content.user-action-dropdown-menu {
  .user-action-option {
    display: flex;
    align-items: flex-start;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    .user-action-option-icon {
      margin-right: 0.575rem;
      color: var(--fts-default);
    }
    .user-action-option-r {
      white-space: normal;
      .user-action-option-helper {
        margin-top: 0.125rem;
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--fts-default);
      }
    }
  }
}
</style>